import type { Candle } from "../types";

// CISD — Change In State of Delivery. Deterministic port of the MT5 research state machine:
// a run of same-colour candles is a "delivery"; when the run ends, the OPEN of the run's first candle is armed
// as the opposing CISD level; the CISD confirms when a later candle CLOSES beyond that level (body close, never wicks).
// No look-ahead: state at index i depends only on candles[0..i]. Confirmed events never repaint.
export type CisdState =
  | "NEUTRAL" | "BULLISH_DELIVERY" | "BEARISH_DELIVERY"
  | "PENDING_BULLISH_CISD" | "PENDING_BEARISH_CISD"
  | "BULLISH_CISD_CONFIRMED" | "BEARISH_CISD_CONFIRMED";

export interface CisdEvent { index: number; from: number; level: number; dir: "bull" | "bear"; time: number }
export interface CisdPending { level: number; from: number; dir: "bull" | "bear" }
export interface CisdResult { state: CisdState; events: CisdEvent[]; pending: CisdPending | null; runDir: "bull" | "bear" | null; runStart: number }

const colour = (c: Candle) => (c.close > c.open ? "bull" : c.close < c.open ? "bear" : null);

export const CisdEngine = {
  analyze(candles: Candle[]): CisdResult {
    const events: CisdEvent[] = [];
    let runDir: "bull" | "bear" | null = null, runStart = 0;
    let pendingBull: CisdPending | null = null, pendingBear: CisdPending | null = null;
    let state: CisdState = "NEUTRAL";
    for (let i = 0; i < candles.length; i++) {
      const c = candles[i], col = colour(c);
      if (col && col !== runDir) {
        if (runDir === "bear") pendingBull = { level: candles[runStart].open, from: runStart, dir: "bull" }; // bearish run ended → arm bullish CISD
        if (runDir === "bull") pendingBear = { level: candles[runStart].open, from: runStart, dir: "bear" }; // bullish run ended → arm bearish CISD
        runDir = col; runStart = i;
        state = col === "bull" ? "BULLISH_DELIVERY" : "BEARISH_DELIVERY";
      }
      if (pendingBull && c.close > pendingBull.level) { events.push({ index: i, from: pendingBull.from, level: pendingBull.level, dir: "bull", time: c.time }); pendingBull = null; state = "BULLISH_CISD_CONFIRMED"; }
      else if (pendingBear && c.close < pendingBear.level) { events.push({ index: i, from: pendingBear.from, level: pendingBear.level, dir: "bear", time: c.time }); pendingBear = null; state = "BEARISH_CISD_CONFIRMED"; }
      else if (state !== "BULLISH_CISD_CONFIRMED" && state !== "BEARISH_CISD_CONFIRMED" || col) {
        if (pendingBull && runDir === "bull") state = "PENDING_BULLISH_CISD";
        else if (pendingBear && runDir === "bear") state = "PENDING_BEARISH_CISD";
      }
    }
    const pending = runDir === "bull" ? pendingBull : runDir === "bear" ? pendingBear : (pendingBull ?? pendingBear);
    return { state, events, pending, runDir, runStart };
  },
};
